import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { JoystickIcon } from "@/assets/icons/JoystickIcon";
import { usePTZSession } from "@/hooks/usePTZSession";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useRemPx } from "@/hooks/useRemPx";
import { PTZJoystick } from "./PTZJoystick";

/**
 * 조이스틱 지름. 큰 컨트롤러는 5.5rem.
 *
 * 폰과 데스크톱 좁은 타일을 가른다 — 폰에서는 손가락이 닿을 크기가 필요하지만,
 * 2×2 타일에 얹히는 팝오버는 영상을 덜 가릴수록 낫다.
 */
const JOYSTICK_REM = { mobile: 4, desktop: 3.25 };

/**
 * 속도 3단. 큰 컨트롤러는 1~5 슬라이더지만, 좁은 화면에서 5단 슬라이더는 못 집는다.
 * 값은 같은 1~5 척도를 쓰므로 두 컨트롤러를 오가도 의미가 어긋나지 않는다.
 */
const SPEED_STEPS: { label: string; value: number }[] = [
  { label: "느림", value: 1 },
  { label: "보통", value: 3 },
  { label: "빠름", value: 5 },
];

interface PTZControlCompactProps {
  /** 제어 대상 CCTV id — POST /cctvs/{id}/ptz */
  cctvId: number;
  /** 데스크톱 팝오버가 넘지 않아야 할 높이(px). 타일이 짧을 때 넘쳐 나가는 것을 막는다 */
  maxHeight?: number;
}

/**
 * 좁은 타일·모바일용 PTZ 컨트롤러.
 *
 * 큰 컨트롤러(`PTZControl`)와 **제어 로직은 같다**(`usePTZSession`). 다른 것은 자리가
 * 좁아 덜어낸 것들이다.
 *
 * **프리셋 저장·삭제를 뺐다** — 이름 입력 텍스트 필드가 필요한데, iOS 에서 고정 오버레이
 * 위의 키보드는 레이아웃을 밀어 놓고 돌아오지 않는다(#269 진단). 폰에서 프리셋을 새로
 * 만드는 일은 드물고 그건 관제 PC 에서 한다. 여기서는 **저장된 위치로 가는 것**만 된다.
 *
 * 모바일에서는 팝오버 대신 바텀시트로 뜬다 — 타일 우하단은 네이티브 video 컨트롤 바와
 * 하단 탭바가 쓴다.
 */
export function PTZControlCompact({ cctvId, maxHeight }: PTZControlCompactProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const pxPerRem = useRemPx();

  const panelRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);

  const {
    speed,
    setSpeed,
    error,
    setDesiredPanTilt,
    setDesiredZoom,
    presets,
    presetsLoading,
    presetBusy,
    gotoPreset,
  } = usePTZSession(cctvId, open);

  // 바깥 클릭 시 닫기 (데스크톱 팝오버). 모바일은 백드롭이 대신한다.
  useEffect(() => {
    if (!open || isMobile) return;

    function handleClickOutside(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        fabRef.current &&
        !fabRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [open, isMobile]);

  const zoomButton = (dir: 1 | -1, label: string, path: string) => (
    <button
      type="button"
      className={`flex items-center justify-center rounded-md bg-neutral-100 text-neutral-600 transition-colors hover:bg-neutral-200 active:bg-brand/30 dark:bg-[#252833] dark:text-white/70 dark:hover:bg-[#2A2D3A] ${isMobile ? "h-9 w-9" : "h-7 w-7"}`}
      onPointerDown={() => setDesiredZoom(dir)}
      onPointerUp={() => setDesiredZoom(0)}
      onPointerLeave={() => setDesiredZoom(0)}
      onPointerCancel={() => setDesiredZoom(0)}
      title={`${label} (누르고 있는 동안)`}
      aria-label={label}
    >
      <svg
        className="h-4 w-4"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" d={path} />
      </svg>
    </button>
  );

  const body = (
    <>
      {/* 조이스틱 + 줌 — 줄어들지 않는다. 프리셋이 많아도 이건 늘 보여야 한다 */}
      <div className="flex flex-none items-center gap-3 px-4 py-3">
        <div className="flex flex-1 justify-center">
          <PTZJoystick
            size={Math.round(JOYSTICK_REM[isMobile ? "mobile" : "desktop"] * pxPerRem)}
            onDirectionChange={setDesiredPanTilt}
          />
        </div>
        <div className="flex flex-col items-center gap-2">
          <span className="text-[0.6875rem] font-medium text-neutral-500 dark:text-white/40">
            ZOOM
          </span>
          {zoomButton(1, "Zoom In", "M12 6v12M6 12h12")}
          {zoomButton(-1, "Zoom Out", "M6 12h12")}
        </div>
      </div>

      {/* 속도 — 슬라이더 대신 3단 칩 */}
      <div className="flex flex-none items-center gap-2 border-t border-neutral-200 dark:border-white/5 px-4 py-2.5">
        <span className="text-[0.6875rem] font-medium text-neutral-500 dark:text-white/40">
          속도
        </span>
        <div className="flex flex-1 gap-1.5">
          {SPEED_STEPS.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setSpeed(s.value)}
              aria-pressed={speed === s.value}
              className={`h-8 flex-1 rounded-md text-xs font-medium transition-colors ${
                speed === s.value
                  ? "bg-brand/20 text-brand"
                  : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-[#252833] dark:text-white/70 dark:hover:bg-[#2A2D3A]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/*
        프리셋 — 이동만. 저장·삭제는 관제 PC 에서 한다.

        **한 줄 가로 스크롤이다.** 이 컨트롤러가 뜨는 자리(2×2 타일·폰)는 세로가 병목이라,
        줄바꿈하면 개수에 따라 높이가 들쭉날쭉해지고 영상을 더 가린다. 한 줄이면 몇 개든
        높이가 같다. 큰 컨트롤러는 세로가 넉넉해 지금처럼 줄바꿈으로 둔다.

        개수를 라벨에 적는다 — 가로 스크롤은 남은 것이 안 보여서, 숫자가 없으면 끝인 줄 안다.
      */}
      <div className="flex-none border-t border-neutral-200 dark:border-white/5 px-4 py-2.5">
        <span className="mb-1.5 block text-[0.6875rem] font-medium text-neutral-500 dark:text-white/40">
          프리셋{presets.length > 0 && ` (${presets.length})`}
        </span>
        {presetsLoading ? (
          <div className="py-1 text-xs text-neutral-400 dark:text-white/30">불러오는 중…</div>
        ) : presets.length === 0 ? (
          <div className="py-1 text-xs leading-relaxed text-neutral-400 dark:text-white/30">
            저장된 프리셋이 없습니다. 프리셋은 관제 PC 에서 저장할 수 있습니다.
          </div>
        ) : (
          // `snap` 을 두어 손을 떼면 칩 경계에 선다 — 반쯤 잘린 칩이 남지 않는다.
          <div className="flex snap-x snap-mandatory gap-1.5 overflow-x-auto pb-0.5">
            {presets.map((p) => (
              <button
                key={p.token}
                type="button"
                disabled={presetBusy}
                onClick={() => gotoPreset(p.token)}
                title={`'${p.name}' 위치로 이동`}
                className="h-8 max-w-[8rem] shrink-0 snap-start truncate rounded-lg bg-neutral-100 px-3 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-200 active:bg-brand/30 disabled:opacity-50 dark:bg-[#252833] dark:text-white/70 dark:hover:bg-[#2A2D3A]"
              >
                {p.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="border-t border-neutral-200 px-4 py-2 text-xs leading-relaxed text-red-400 dark:border-white/5">
          {error}
        </div>
      )}
    </>
  );

  return (
    <div className="relative">
      {open &&
        (isMobile ? (
          // body 로 뽑아낸다. 모바일 셸이 지면을 `overflow-y-auto` 안에 넣고 하단 탭 바를
          // 형제로 두기 때문에, 여기서 그냥 `fixed` 로 두면 시트가 그 안에 갇혀 탭 바와
          // 알람 FAB(바 위로 튀어나온 것)에 가린다. `AlarmInboxBell` 도 같은 이유로 포털을 쓴다.
          createPortal(
            <>
              {/* 백드롭을 두지 않는다 — 카메라를 돌리는 동안 **영상을 봐야 한다.**
                  화면을 덮으면 조작의 결과가 안 보여 목적 자체가 사라진다.
                  닫기는 헤더의 X 로 한다. 시트 바깥은 그대로 눌린다. */}
              <div
                ref={panelRef}
                role="dialog"
                aria-label="카메라 PTZ 제어"
                // 세로는 화면 기준으로 자른다 — 시트는 타일이 아니라 뷰포트에 붙는다.
                className="fixed inset-x-0 bottom-0 z-[1000] flex max-h-[70dvh] flex-col overflow-y-auto rounded-t-2xl bg-white/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-0.25rem_1.5rem_rgba(0,0,0,0.5)] backdrop-blur-md dark:bg-[#1A1D27]/95"
              >
                <div className="flex items-center gap-2 px-4 pb-2 pt-3 text-xs font-semibold text-neutral-600 dark:text-white/70">
                  <JoystickIcon size={14} />
                  카메라 제어
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="ml-auto -mr-1 rounded p-1 text-neutral-400 hover:text-neutral-700 dark:text-white/40 dark:hover:text-white"
                    aria-label="닫기"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
                    </svg>
                  </button>
                </div>
                {body}
              </div>
            </>,
            document.body
          )
        ) : (
          <div
            ref={panelRef}
            className="absolute bottom-[3.25rem] right-0 flex w-[13.5rem] flex-col overflow-y-auto rounded-xl bg-white/90 shadow-[0_4px_24px_rgba(0,0,0,0.5)] backdrop-blur-md dark:bg-[#1A1D27]/90"
            style={{ border: "1px solid rgba(255,255,255,0.08)", maxHeight }}
          >
            <div className="flex items-center gap-2 px-4 pb-2 pt-3 text-xs font-semibold text-neutral-600 dark:text-white/70">
              <JoystickIcon size={14} />
              카메라 제어
            </div>
            {body}
          </div>
        ))}

      {/* FAB — 큰 컨트롤러(3.5rem)보다 작다 */}
      <button
        ref={fabRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`relative z-10 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full border text-white transition-all duration-200 ${
          open
            ? "border-brand bg-brand shadow-[0_0_0.75rem_rgba(255,117,0,0.6)]"
            : "border-white/40 bg-brand backdrop-blur-sm"
        }`}
        title="카메라 PTZ 제어"
        aria-label="카메라 PTZ 제어"
      >
        <JoystickIcon size={20} />
      </button>
    </div>
  );
}
